import { Component, inject, signal, OnInit, PLATFORM_ID } from '@angular/core';
import { CommonModule, isPlatformBrowser, Location } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { CardModule } from 'primeng/card';
import { ButtonModule } from 'primeng/button';
import { TagModule } from 'primeng/tag';
import { DialogModule } from 'primeng/dialog';

import { PaymentService, MemberPayment } from '../../../services/payment';
import { ReceiptDownloader } from '../../../services/receipt-downloader';
import { AuthService } from '../../../services/auth';
import { ToastService } from '../../../services/toast';
import { PageTitleService } from '../../../services/page-title';
import { IndDatePipe } from '../../../pipes/ind-date';
import { ReceiptView } from '../receipt/receipt-view';

type Severity = 'success' | 'danger' | 'warn' | 'info' | 'secondary';

/**
 * Payment Details — a full page for ONE payment of ANY status (success / pending /
 * failed / cancelled / refunded). Fetched by id from MemberPayment/Detail, which is
 * scoped server-side (admin sees any; a partner only their own member's payment). When
 * the payment is successful, the receipt can be viewed / printed / downloaded here.
 * Reached from the global search, the payments grid, the members grid and member-lookup.
 */
@Component({
  selector: 'app-payment-detail',
  standalone: true,
  imports: [CommonModule, CardModule, ButtonModule, TagModule, DialogModule, IndDatePipe, ReceiptView],
  templateUrl: './payment-detail.html',
  styleUrls: ['./payment-detail.css'],
})
export class PaymentDetailPage implements OnInit {
  private svc = inject(PaymentService);
  private receipts = inject(ReceiptDownloader);
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private location = inject(Location);
  private auth = inject(AuthService);
  private toast = inject(ToastService);
  private platformId = inject(PLATFORM_ID);
  readonly pageTitle = inject(PageTitleService).title;

  loading = signal(false);
  rechecking = signal(false);
  payment = signal<MemberPayment | null>(null);
  notFound = signal(false);
  showReceipt = signal(false);

  get isAdmin(): boolean {
    return this.auth.isAdmin();
  }

  ngOnInit() {
    if (!isPlatformBrowser(this.platformId)) return;
    // Subscribe (not snapshot) so navigating to another payment while already on this page reloads.
    this.route.paramMap.subscribe((pm) => {
      const id = Number(pm.get('id'));
      if (Number.isInteger(id) && id > 0) this.load(id);
      else this.notFound.set(true);
    });
  }

  load(id: number) {
    this.loading.set(true);
    this.notFound.set(false);
    this.payment.set(null);
    this.svc.payGetDetail(id).subscribe({
      next: (res: any) => {
        this.loading.set(false);
        if (res?.success && res.data) {
          const p = res.data as MemberPayment;
          this.payment.set(p);
          // This is the screen support opens when told "the money was taken but it says pending".
          // /Detail does not reconcile, so on its own it would render PENDING forever. Re-check the
          // gateway automatically for a still-open payment — that is the whole reason to be here.
          if (this.isOpen(p)) this.recheck(true);
        } else this.notFound.set(true);
      },
      error: () => {
        this.loading.set(false);
        this.notFound.set(true);
      },
    });
  }

  /** Still awaiting a terminal money event, so worth asking the gateway about. */
  isOpen(p: MemberPayment | null): boolean {
    const st = (p?.paymentStatus || '').toUpperCase();
    return st === 'PENDING' || st === 'INITIATED';
  }

  /**
   * Ask the gateway for this payment's real status. GET /MemberPayment/GetByPaymentUniqueId runs
   * ReconcileIfPaid server-side, so a payment that really was captured gets confirmed and its
   * policy issued. `silent` suppresses the toast when this runs automatically on load.
   */
  recheck(silent = false) {
    const p = this.payment();
    const uid = p?.paymentUniqueId;
    if (!uid || this.rechecking()) return;

    this.rechecking.set(true);
    this.svc.payGetByUid(uid).subscribe({
      next: (res: any) => {
        this.rechecking.set(false);
        const fresh = res?.data as MemberPayment | undefined;
        if (!fresh) {
          if (!silent) this.toast.error('Could not re-check this payment with the gateway.');
          return;
        }
        const was = (p?.paymentStatus || '').toUpperCase();
        const now = (fresh.paymentStatus || '').toUpperCase();
        this.payment.set(fresh);
        if (now === 'SUCCESS' && was !== 'SUCCESS')
          this.toast.success('The gateway had this payment as captured — it is now confirmed.');
        else if (!silent)
          this.toast.info(`The gateway still reports this payment as ${now || 'unknown'}.`);
      },
      error: () => {
        this.rechecking.set(false);
        if (!silent) this.toast.error('Could not re-check this payment with the gateway.');
      },
    });
  }

  // ===================== display helpers =====================
  isPaid(p: MemberPayment): boolean {
    return (p.paymentStatus || '').toUpperCase() === 'SUCCESS';
  }

  statusSeverity(s?: string | null): Severity {
    switch ((s || '').toUpperCase()) {
      case 'SUCCESS':
        return 'success';
      case 'FAILED':
      case 'CANCELLED':
        return 'danger';
      case 'REFUNDED':
        return 'info';
      case 'PENDING':
      case 'INITIATED':
        return 'warn';
      default:
        return 'secondary';
    }
  }

  payerLabel(p: MemberPayment): string {
    if (p.payerType === 'PARTNER') return `PARTNER${p.partnerId ? ' #' + p.partnerId : ''}`;
    return `CLIENT${p.clientRef ? ' · ' + p.clientRef : ''}`;
  }

  prettyJson(s?: string | null): string {
    if (!s) return '';
    try {
      return JSON.stringify(JSON.parse(s), null, 2);
    } catch {
      return s;
    }
  }

  // ===================== actions =====================
  viewReceipt() {
    this.showReceipt.set(true);
  }
  downloadReceipt(p: MemberPayment) {
    this.receipts.downloadPdf(p.id, p.paymentUniqueId);
  }
  printReceipt(p: MemberPayment) {
    this.receipts.printHtml(p.id);
  }

  openMember(p: MemberPayment) {
    this.router.navigate(['/member-lookup'], {
      queryParams: { q: p.memberUniqueId || String(p.memberId) },
    });
  }

  back() {
    this.location.back();
  }
}
